import React, { useState } from "react";
import { Modal, Button, Table, Form, Message, Icon } from "semantic-ui-react";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "../../../../../../apis/dvdlocator";

export const AdjustInventory = ({
  onGridRefresh,
  open,
  close,
  initialUnits,
  userId,
}) => {
  const [inventory, setInventory] = useState(initialUnits);
  const [success, setSuccess] = useState(null);
  const invalidRows = inventory.some(
    (unit) => !unit.supply.upc || !unit.supply.inventory_id
  );
  const [requestError, setRequestError] = useState(null);
  const handleClose = () => {
    if (success) {
      onGridRefresh();
    }

    close();
  };
  const schema = yup.object().shape(
    inventory.reduce((acc, unit) => {
      acc[unit.supply.upc] = yup
        .number()
        .typeError("Please enter a number.")
        .required()
        .moreThan(-1, "Can't use negative values.")
        .not([unit.inventory.inv], "No adjustment was made.");
      return acc;
    }, {})
  );

  const formik = useFormik({
    initialValues: inventory.reduce((acc, unit) => {
      acc[unit.supply.upc] = unit.inventory.inv;
      return acc;
    }, {}),
    validationSchema: schema,

    onSubmit: async (values) => {
      setRequestError(null);
      setSuccess(null);
      const adjustments = Object.entries(values).map(
        ([upc, newInventoryValue]) => ({
          upc,
          newInventoryValue,
        })
      );

      const body = {
        userId,
        adjustments,
      };

      try {
        await axios.post("/inventory/adjustments", body);

        setSuccess(true);

        setInventory((units) =>
          units.map((unit) => {
            // Finding the adjustment for this upc
            const adjustmentRow = adjustments.find(
              (s) => s.upc === unit.supply.upc
            );
            // Updating the inv value to the new value
            return {
              ...unit,
              inventory: {
                inv: adjustmentRow.newInventoryValue,
              },
            };
          })
        );
      } catch (err) {
        setRequestError(err);
      }
    },
  });

  return (
    <Modal open={open} onClose={handleClose}>
      {success && <Message success header={"Submission was successful!"} />}
      {requestError && (
        <Message
          error
          header={"Submission failed!"}
          content={JSON.stringify(requestError.message || "error!")}
        />
      )}
      {invalidRows && (
        <Message
          warning
          header={"Invalid rows!"}
          content={
            "Some rows don't have a UPC or Inventory ID. Please deselect and try again."
          }
        />
      )}

      <Modal.Header>Adjust Inventory</Modal.Header>
      <Modal.Content>
        <Form
          onSubmit={formik.handleSubmit}
          onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
        >
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  UPC(s){" "}
                  <Icon
                    name="help circle"
                    color="grey"
                    title="May display one or more if row has equivalent UPCs"
                    style={{ cursor: "pointer" }}
                  />
                </Table.HeaderCell>
                <Table.HeaderCell>Title</Table.HeaderCell>
                <Table.HeaderCell>Current Inventory</Table.HeaderCell>
                <Table.HeaderCell>New Inventory</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {inventory.map((unit) => (
                <Table.Row
                  key={unit.supply.upc}
                  error={!unit.supply.upc || !unit.supply.inventory_id}
                >
                  <Table.Cell>
                    {unit.equivalent_upcs_view.equivalent_upcs
                      ? unit.equivalent_upcs_view.equivalent_upcs
                          .split(",")
                          .join(", ")
                      : unit.supply.upc}
                  </Table.Cell>
                  <Table.Cell>{unit.supply.supply_title}</Table.Cell>
                  <Table.Cell>{unit.inventory.inv}</Table.Cell>
                  <Table.Cell>
                    <Form.Input
                      name={unit.supply.upc}
                      type="number"
                      placeholder="Enter new inventory"
                      value={formik.values[unit.supply.upc]}
                      onChange={formik.handleChange}
                      error={
                        formik.touched[unit.supply.upc] &&
                        formik.errors[unit.supply.upc]
                          ? formik.errors[unit.supply.upc]
                          : null
                      }
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button color="grey" onClick={handleClose}>
              Close
            </Button>
            <Button
              color="blue"
              type="submit"
              disabled={formik.isSubmitting || invalidRows}
            >
              Submit
            </Button>
          </div>
        </Form>
      </Modal.Content>
    </Modal>
  );
};
